import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DropResult } from "react-beautiful-dnd";
import * as erState from "../../reducers/errorReducer";
import ErrorFetching from "../../components/modals/ErrorFetching";
import Pagination from "../../components/utils/Pagination";
import SearchBar from "../../components/utils/SearchBar";
import { useNotify } from "../../hooks/useNotify";
import { CertParamTypes, HeaderObject } from "../../types/types";
import {
  move,
  insert,
  getPageList,
  toggleKey,
  sortByKey,
  searchArrayByValue,
  mapFromKeys,
  getUniqueKeysForTable,
} from "../../utils/functions";
import { CertHeader } from "./CertHeader";
import { CertTableRow } from "./CertTableRow";
import { useParams } from "react-router-dom";
import allActions from "../../actions/allActions";

interface TableProps {
  list: any[];
  headerRow: string[];
}

export const CertTable: React.FC<TableProps> = ({ list, headerRow }) => {
  const { message } = useSelector(erState.getErrorMessage);
  const { certOperationId } = useParams<CertParamTypes>();
  const errorMsg = useNotify(message);
  const filterDivToggleId = "CERT_DETAILS_TABLE_FILTER";
  const tableSettingsDivId = "CERT_DETAILS_SETTINGS";
  const exportSettingsDivId = "CERT_DETAILS_EXPORT_SETTINGS";
  const pageLimit = 9;
  const dispatch = useDispatch();

  const [state, setState] = useState({
    currentPage: 1,
    totalPages: Math.ceil(list.length / pageLimit),
    pageLimit: pageLimit,
    totalRecords: list.length,
    pageNeighbours: 0,
    filteredList: list,
    currentList: list.slice(0, pageLimit),
    filterKeys: [],
    searchWord: "",
    sortKey: headerRow[0],
    ascending: false,
    possibleSearchKeys: list.length ? Object.keys(list[0]) : [],
    headers: {
      droppableColumnsSelected: headerRow,
      droppableColumnsAll: list.length
        ? getUniqueKeysForTable(list).filter(
            (item) => !headerRow.includes(item)
          )
        : [],
    },
    export: {
      exportAll: {
        data: mapFromKeys(list, getUniqueKeysForTable(list)),
        filename: "cert_details",
        label: "export all",
      },
      exportView: {
        data: mapFromKeys(list, headerRow),
        filename: "cert_view_details",
        label: "export view",
      },
    },
  });

  const onDragEnd = (dragObject: DropResult) => {
    if (dragObject.destination) {
      if (
        dragObject.source.droppableId === dragObject.destination.droppableId
      ) {
        const updated = move(
          state.headers[dragObject.destination.droppableId],
          dragObject.source.index,
          dragObject.destination.index
        );
        const updatedHeaders: HeaderObject = {
          ...state.headers,
          [dragObject.destination.droppableId]: updated,
        };
        const newExportedView =
          dragObject.destination.droppableId === "droppableColumnsSelected"
            ? mapFromKeys(state.filteredList, updated)
            : state.export.exportView.data;
        setState({
          ...state,
          headers: updatedHeaders,
          export: {
            ...state.export,
            exportView: {
              ...state.export.exportView,
              data: newExportedView,
            },
          },
        });
      } else {
        const updatedSource: string[] = state.headers[
          dragObject.source.droppableId
        ].filter((header) => header !== dragObject.draggableId);
        const updatedDestination: string[] = insert(
          state.headers[dragObject.destination.droppableId],
          dragObject.destination.index,
          dragObject.draggableId
        );
        const updatedHeaders: HeaderObject = ({
          [dragObject.source.droppableId]: updatedSource,
          [dragObject.destination.droppableId]: updatedDestination,
        } as unknown) as HeaderObject;
        const newExportedView = mapFromKeys(
          state.filteredList,
          updatedHeaders.droppableColumnsSelected
        );
        setState({
          ...state,
          headers: updatedHeaders,
          export: {
            ...state.export,
            exportView: {
              ...state.export.exportView,
              data: newExportedView,
            },
          },
        });
      }
    }
  };

  const gotoPage = (page: number) => {
    const currentList = getPageList(page, state.pageLimit, state.filteredList);
    const updated = {
      ...state,
      currentPage: page,
      currentList,
    };
    setState(updated);
  };

  const toggleFilter = (key: string) => {
    const updated = {
      ...state,
      filterKeys: toggleKey(key, state.filterKeys),
    };
    setState(updated);
  };

  const goEditCert = (mailId: string) => {
    dispatch(allActions.certActions.mapEditData(certOperationId, mailId));
  };

  const resend = (mailId: string) => {
    dispatch(allActions.certActions.resend(mailId));
  };

  const sort = (key: string) => {
    const sorted = sortByKey(key, state.ascending, state.filteredList);
    const newExportedView = mapFromKeys(
      sorted,
      state.headers.droppableColumnsSelected
    );
    const updated = {
      ...state,
      ascending: !state.ascending,
      sortKey: key,
      filteredList: sorted,
      currentList: sorted.slice(0, state.pageLimit),
      export: {
        ...state.export,
        exportView: {
          ...state.export.exportView,
          data: newExportedView,
        },
      },
    };
    setState(updated);
  };

  const clearFilters = () => {
    const offset = (state.currentPage - 1) * state.pageLimit;
    const filteredList = searchArrayByValue(list, state.searchWord, []);
    const newExportedView = mapFromKeys(
      filteredList,
      state.headers.droppableColumnsSelected
    );
    const updated = {
      ...state,
      filteredList,
      currentList: filteredList.slice(offset, offset + state.pageLimit),
      totalPages: Math.ceil(filteredList.length / state.pageLimit),
      filterKeys: [],
      export: {
        ...state.export,
        exportView: {
          ...state.export.exportView,
          data: newExportedView,
        },
      },
    };
    setState(updated);
  };

  const onSearchWordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const offset = (state.currentPage - 1) * state.pageLimit;
    const filteredList = searchArrayByValue(
      list,
      event.target.value,
      state.filterKeys
    );
    const newExportedView = mapFromKeys(
      filteredList,
      state.headers.droppableColumnsSelected
    );
    const updated = {
      ...state,
      searchWord: event.target.value,
      currentList: filteredList.slice(offset, offset + state.pageLimit),
      filteredList: filteredList,
      totalRecords: filteredList.length,
      totalPages: Math.ceil(filteredList.length / state.pageLimit),
      export: {
        ...state.export,
        exportView: {
          ...state.export.exportView,
          data: newExportedView,
        },
      },
    };
    setState(updated);
  };

  //TODO: Kun painaa sortkey => pidä paginointi kunnossa!!

  return (
    <div className='flex flex-col shadow-lg bg-brand-white md:w-full h-full overflow-y-auto overflow-x-hidden scroller'>
      <ErrorFetching message={errorMsg} />
      <SearchBar
        sortKeys={state.possibleSearchKeys}
        onDragEnd={onDragEnd}
        filterDivToggleId={filterDivToggleId}
        tableSettingsDivToggleId={tableSettingsDivId}
        exportSettingsDivToggleId={exportSettingsDivId}
        selectedColumns={state.headers.droppableColumnsSelected}
        restColumns={state.headers.droppableColumnsAll}
        pageLimit={state.pageLimit}
        activeFilters={state.filterKeys}
        toggleFilter={toggleFilter}
        clearFilters={clearFilters}
        exportSettings={state.export}
        onSearchWordChange={onSearchWordChange}
      />
      <table className='md:bg-white rounded-lg md:table-fixed w-full'>
        <CertHeader
          headers={state.headers.droppableColumnsSelected}
          sort={sort}
        />
        <tbody>
          {state.currentList.map((p, i) => (
            <CertTableRow
              goEditCert={goEditCert}
              resend={resend}
              headerKeys={state.headers.droppableColumnsSelected}
              cert={p}
              key={i}
            />
          ))}
        </tbody>
      </table>
      <Pagination
        pageLimit={state.pageLimit}
        totalPages={state.totalPages}
        pageNeighbours={state.pageNeighbours}
        currentPage={state.currentPage}
        onPageChanged={gotoPage}
      />
    </div>
  );
};

export default CertTable;
